import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, CircularProgress, Stack } from '@mui/material';
import { Close as DeleteIcon, Description as FileIcon, Image as ImageIcon } from '@mui/icons-material';
import useManageAttachment from '../../../_data_sources/useManageAttachment';

interface AttachmentBoxProps {
  attachmentRef: { id: string; embedded: boolean };
  manualId: string;
  isEditable: boolean;
  onRemove: (id: string) => void;
}

const AttachmentBox: React.FC<AttachmentBoxProps> = ({ attachmentRef, manualId, onRemove, isEditable }) => {
  const { getAttachment } = useManageAttachment();
  const [attachment, setAttachment] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const hasFetchedRef = useRef(false);

  const fetchAttachment = async () => {
    try {
      const data = await getAttachment({ manualId, attachmentId: attachmentRef.id });
      setAttachment(data);
    } catch (error) {
      console.error('Failed to fetch attachment details', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchAttachment();
      hasFetchedRef.current = true;
    }
  }, [attachmentRef.id, manualId, getAttachment]);

  let fileIcon = <FileIcon fontSize='small' />;
  if (attachment) {
    const fileExt = attachment.name.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg'].includes(fileExt || '')) {
      fileIcon = <ImageIcon fontSize='small' />;
    }
  }

  const handleDownload = async () => {
    setLoading(true);
    try {
      let data = attachment;
      if (!data || !data.url) {
        data = await getAttachment({ manualId, attachmentId: attachmentRef.id });
        setAttachment(data);
      }
      if (data && data.url) {
        const resp = await fetch(data.url, { method: 'GET' });
        const blob = await resp.blob();
        const fileName = data.name || 'download';
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }
    } catch (error) {
      console.error('Failed to download attachment', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: 1,
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: '200px',
      }}
    >
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
      >
        {loading ? <CircularProgress size={20} /> : fileIcon}
        <Typography
          variant='body2'
          component='a'
          onClick={handleDownload}
          sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
        >
          {loading ? 'Loading...' : attachment ? attachment.name : 'Unknown'}
        </Typography>
      </Stack>
      {!loading && isEditable && (
        <IconButton
          size='small'
          onClick={() => onRemove(attachmentRef.id)}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      )}
    </Box>
  );
};

export default AttachmentBox;
